/**
 * @props className (optional): 'mt20' (String)
 * @props label (optional): 'Typography' (String)
 * @props typography (required): {fontFamily, fontWeight, fontSize, fontStyle, textTransform, textDecoration, lineHeight, letterSpace} (Object)
 * @props onChange (required): (Function)
 * @props defaults (optional): { fontFamily, fontWeight, fontSize, fontStyle, textTransform, textDecoration, lineHeight, letterSpace } (Object)
 */

import { __ } from '@wordpress/i18n';
import { useState, useEffect } from '@wordpress/element';
import { Button, Dropdown, PanelRow, SelectControl, __experimentalUnitControl as UnitControl, RangeControl } from '@wordpress/components';

import './Typography.scss';
import Title from './Title';
import BtnGroup from './BtnGroup';
import options from './Helper/options';
const { fontStyles, textTransforms, textDecorations, pxUnit, perUnit, emUnit, remUnit } = options;

import fontLists from './Helper/fontLists';

const Typography = props => {
	const { className = '', label = __('Typography:', 'bplugins'), typography, onChange, defaults = {} } = props;

	const defaultVal = { fontFamily: 'Default', fontCategory: 'sans-serif', fontWeight: 400, fontSize: 15, fontStyle: 'normal', textTransform: 'none', textDecoration: 'auto', lineHeight: '135%', letterSpace: '0px' }

	const getDefault = property => defaults?.[property] || defaultVal[property];
	const setDefault = property => onChange({ ...typography, [property]: getDefault(property) });

	const getValue = property => typography?.[property] || getDefault(property);
	const setValue = (property, val) => onChange({ ...typography, [property]: val });
	const resetValue = property => typography?.[property] && typography?.[property] !== getDefault(property) && <Button icon='image-rotate' className='bPlResetVal' onClick={() => setDefault(property)} />

	// Font family searching
	const [query, setQuery] = useState('');
	const [isSearching, setIsSearching] = useState(false);
	const searchFonts = fontLists.filter(({ family }) => family.toLowerCase().includes(query.toLowerCase()));

	// Get Font weights
	const fontWeights = () => {
		const currentFontWeights = fontLists.filter(font => font.family === getValue('fontFamily'))[0]?.variants;
		let weights = []
		currentFontWeights?.map(weight => weights?.push({ label: weight, value: weight }));
		return 0 === weights.length ? [400] : weights;
	}

	// Add styles and google font to typography
	const generateCss = (value, cssProperty, def) => undefined == value ? '' : value === def ? '' : `${cssProperty}: ${value};`;

	useEffect(() => {
		const allStyles = `${undefined == getValue('fontFamily') ? '' : getValue('fontFamily') === defaultVal.fontFamily ? '' : `font-family: '${getValue('fontFamily')}', ${getValue('fontCategory')};`}
		${generateCss(getValue('fontWeight'), 'font-weight', defaultVal.fontWeight)}
		${undefined == getValue('fontSize') ? '' : `font-size: ${getValue('fontSize')}px;`}
		${generateCss(getValue('fontStyle'), 'font-style', defaultVal.fontStyle)}
		${generateCss(getValue('textTransform'), 'text-transform', defaultVal.textTransform)}
		${generateCss(getValue('textDecoration'), 'text-decoration', defaultVal.textDecoration)}
		${undefined == getValue('lineHeight') ? '' : `line-height: ${getValue('lineHeight')};`}
		${generateCss(getValue('letterSpace'), 'letter-spacing', defaultVal.letterSpace)}`;

		// Google font link
		const linkQuery = undefined === getValue('fontVariant') || 400 === getValue('fontVariant') ? '' : '400i' === getValue('fontVariant') ? ':ital@1' : getValue('fontVariant')?.includes('00i') ? `: ital, wght@1, ${getValue('fontVariant')?.replace('00i', '00')} ` : `: wght@${getValue('fontVariant')} `;

		const link = 'Default' === getValue('fontFamily') || undefined === getValue('fontFamily') ? '' : `https://fonts.googleapis.com/css2?family=${getValue('fontFamily')?.split(' ').join('+')}${linkQuery.replace(/ /g, '')}&display=swap`;

		onChange({ ...typography, ['styles']: allStyles.replace(/\s+/g, ' ').trim(), ['googleFontLink']: link });
	}, [typography?.fontFamily, typography?.fontWeight, typography?.fontSize, typography?.fontStyle, typography?.textTransform, typography?.textDecoration, typography?.lineHeight, typography?.letterSpace]);

	return <PanelRow className={`bPlDropdown ${className}`}>
		<Title mt='0' mb='0'>{label}</Title>

		<Dropdown className='bPlDropdownContainer' contentClassName='bPlDropdownPopover' position='bottom right'
			renderToggle={({ isOpen, onToggle }) => <Button icon='admin-customizer' onClick={onToggle} aria-expanded={isOpen} />}
			renderContent={() => <>
				{/* Font Family & Weight */}
				<PanelRow className='bPlTypoFontTitle'>
					<Title mt='0' mb='0'>{__('Font Family:', 'bplugins')}</Title>
					<Title mt='0' mb='0'>{__('Weight:', 'bplugins')}</Title>
				</PanelRow>
				<PanelRow className='bPlTypoFont'>
					<div className='bPlTypoFontSelect'>
						<input type='search' value={query} onClick={() => setIsSearching(!isSearching)} placeholder={getValue('fontFamily') || 'Search Font'} onChange={e => setQuery(e.target.value)} />

						<span className={`dashicon dashicons dashicons-${isSearching ? 'arrow-up' : 'arrow-down'} `} onClick={() => setIsSearching(!isSearching)}></span>

						{isSearching && <ul className='bPlTypoFontLists'>
							{searchFonts?.map(font => <li key={font?.family} onClick={() => {
								onChange({ ...typography, ['fontFamily']: font?.family, ['fontCategory']: font?.category || 'sans-serif', ['fontWeight']: 400, ['fontVariant']: 400 });
								setQuery('');
								setIsSearching(false);
							}}>{font?.family}</li>)}
						</ul>}
					</div>

					<SelectControl value={getValue('fontVariant')} onChange={val => { onChange({ ...typography, ['fontWeight']: parseInt(val?.replace('00i', '00')), ['fontVariant']: val }) }} options={fontWeights()} />
				</PanelRow>

				{/* Font Size */}
				<Title>{__('Font Size:', 'bplugins')}</Title>
				<RangeControl value={getValue('fontSize')} onChange={val => setValue('fontSize', val)} min={0} max={120} step={1} allowReset={true} resetFallbackValue={defaults?.fontSize || defaultVal.fontSize} initialPosition={defaults?.fontSize || defaultVal.fontSize} />

				{/* Font Style */}
				<PanelRow className='mt20'>
					<Title mt='0' mb='0'>{__('Font Style:', 'bplugins')}</Title>
					<SelectControl value={getValue('fontStyle')} onChange={val => setValue('fontStyle', val)} options={fontStyles} />
					{resetValue('fontStyle')}
				</PanelRow>

				{/* Text Transform */}
				<PanelRow className='mt20'>
					<Title mt='5px'>{__('Text Transform:', 'bplugins')}</Title>
					<BtnGroup value={getValue('textTransform')} onChange={val => setValue('textTransform', val)} options={textTransforms} isTextIcon={true} />
				</PanelRow>

				{/* Text Decoration */}
				<PanelRow className='mt20'>
					<Title mt='0' mb='0'>{__('Text Decoration:', 'bplugins')}</Title>
					<SelectControl value={getValue('textDecoration')} onChange={val => setValue('textDecoration', val)} options={textDecorations} />
					{resetValue('textDecoration')}
				</PanelRow>

				{/* Line Height */}
				<PanelRow className='mt20'>
					<UnitControl label={__('Line Height:', 'bplugins')} labelPosition='left' value={getValue('lineHeight')} onChange={val => setValue('lineHeight', val)} units={[pxUnit, perUnit, emUnit, remUnit]} />
					{resetValue('lineHeight')}
				</PanelRow>

				{/* Letter Spacing */}
				<PanelRow className='mt20'>
					<UnitControl label={__('Letter Spacing:', 'bplugins')} labelPosition='left' value={getValue('letterSpace')} onChange={val => setValue('letterSpace', val)} units={[pxUnit, emUnit, remUnit]} />
					{resetValue('letterSpace')}
				</PanelRow>
			</>}
		/>
	</PanelRow>;
};
export default Typography;